import { Link, useLocation, useNavigate } from "react-router-dom";
import { PawPrint, Dog, Calendar, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import LanguageToggle from "@/components/LanguageToggle";
import MobileNavMenu from "@/components/MobileNavMenu";

/**
 * Unified public site navigation bar.
 * Renders the same icon+label links on every public page (Dog / Calendar / Shield + text-only About Us),
 * language toggle, primary CTA and the mobile drawer trigger.
 *
 * @param {string}  activeKey  One of: "canlar" | "etkinlikler" | "seffaflik" | "hakkimizda" | undefined
 * @param {boolean} showCta    Whether to render the "Canları Keşfet / Discover" primary CTA on desktop (default: true)
 * @param {ReactNode} rightSlot Optional extra content placed before the mobile menu (e.g. share popover)
 */
export default function PublicNav({ activeKey, showCta = true, rightSlot = null }) {
  const { t } = useContent();
  const L = useT();
  const location = useLocation();
  const navigate = useNavigate();

  // When the KAHU logo is clicked while already on "/", React Router keeps the
  // pathname the same and skips its normal scroll-to-top. Do it manually so
  // users always land at the hero. If a hash was set (e.g. #hakkimizda), clear
  // it via navigate so the LandingPage's hash-scroll effect cleans up its
  // pending retry timers and doesn't fight our scroll-to-top.
  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      if (location.hash) {
        navigate("/", { replace: true });
      }
      // Defer scroll so React Router state settles first (avoids the effect re-running with old hash).
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  const linkBase = "transition-colors flex items-center gap-1.5 whitespace-nowrap";
  const inactive = "text-muted-foreground hover:text-foreground";
  const active = "text-foreground font-medium";

  return (
    <nav className="glass-nav sticky top-0 z-50 border-b border-border/50" data-testid="public-nav">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2" data-testid="public-nav-home">
            <PawPrint className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-2xl font-semibold text-foreground">KAHU</span>
          </Link>

          <div className="hidden lg:flex items-center gap-7 xl:gap-8">
            <Link
              to="/canlar"
              className={`${linkBase} ${activeKey === "canlar" ? active : inactive}`}
              data-testid="public-nav-canlar"
            >
              <Dog className="w-4 h-4 shrink-0" />
              {L(t("nav.canlar", "Canlarımız"))}
            </Link>
            <Link
              to="/etkinlikler"
              className={`${linkBase} ${activeKey === "etkinlikler" ? active : inactive}`}
              data-testid="public-nav-etkinlikler"
            >
              <Calendar className="w-4 h-4 shrink-0" />
              {L(t("nav.etkinlikler", "Etkinlikler"))}
            </Link>
            <Link
              to="/seffaflik"
              className={`${linkBase} ${activeKey === "seffaflik" ? active : inactive}`}
              data-testid="public-nav-seffaflik"
            >
              <Shield className="w-4 h-4 shrink-0" />
              {L(t("nav.seffaflik", "Şeffaflık"))}
            </Link>
            <Link
              to="/#hakkimizda"
              className={`${activeKey === "hakkimizda" ? active : inactive} transition-colors whitespace-nowrap`}
              data-testid="public-nav-hakkimizda"
            >
              {L(t("nav.hakkimizda", "Hakkımızda"))}
            </Link>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <LanguageToggle />
            {rightSlot}
            {showCta && (
              <Link to="/canlar" className="hidden sm:block">
                <Button className="btn-pill btn-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20 whitespace-nowrap transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30">
                  {t("nav.cta", L("Canları Keşfet"))}
                </Button>
              </Link>
            )}
            <MobileNavMenu />
          </div>
        </div>
      </div>
    </nav>
  );
}
